import { useEffect, useCallback, useRef, useState, useLayoutEffect } from "react";
import {
  DragDropContext,
  Droppable,
  DropResult,
  DragStart,
} from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "hooks";
import {
  fetchProjectBoard,
  setDestroy,
  setFilterValue,
  moveProjectThunk,
  moveColumnThunk,
  addColumnThunk,
  setCardOutcomeThunk,
} from "./project-board-container.slice";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import BoardColumn from "components/project/ui/BoardColumn";
import AddListComposer from "components/project/ui/AddListComposer";
import OutcomeDropBar, {
  ACTION_MOVE_TO,
  parseOutcomeDropId,
} from "components/project/ui/OutcomeDropBar";
import { useProjectBoardSocket } from "features/project-board/use-project-board-socket";
import BoardSkeleton from "components/project/ui/BoardSkeleton";
import BoardAgendaPanel from "components/project/ui/BoardAgendaPanel";
import { PHONE_BAR_HEIGHT_VAR } from "components/layout/phone-bar-offset";
import ProjectBoardHeader from "./project-board-header";
import "./style.scss";

const EMPTY: string[] = [];
const PHONE_BAR_HEIGHT_CSS = `var(${PHONE_BAR_HEIGHT_VAR}, 0px)`;

function ProjectBoardContainer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projectBoardId = "", companyId = "" } = useParams();

  const boardName = useAppSelector((s) => s.projectBoardReducer.boardName);
  const isLoading = useAppSelector((s) => s.projectBoardReducer.isLoading);
  const isError = useAppSelector((s) => s.projectBoardReducer.isError);
  const columnOrder = useAppSelector(
    (s) => s.projectBoardReducer.columnOrder || EMPTY
  );
  const filtersValue = useAppSelector((s) => s.projectBoardReducer.filtersValue);
  const dealCount = useAppSelector(
    (s) =>
      Object.values(s.projectBoardReducer.cardsById || {}).filter(
        (card) => card && !card.__optimistic && !card.outcome && !card.isResolved
      ).length
  );

  const [isDraggingCard, setIsDraggingCard] = useState(false);
  const [flashColumnId, setFlashColumnId] = useState<string | null>(null);
  const [agendaOpen, setAgendaOpen] = useState(false);

  // Live multi-user sync for this board.
  useProjectBoardSocket(companyId, projectBoardId);

  useEffect(() => {
    dispatch(fetchProjectBoard(companyId, projectBoardId, filtersValue));
  }, [companyId, projectBoardId, filtersValue, dispatch]);

  useEffect(
    () => () => {
      dispatch(setDestroy());
    },
    [dispatch]
  );

  const handleFilterChange = (value: unknown, key: string) => {
    dispatch(setFilterValue({ key, value }));
  };

  // Fill exactly from the board's top to the viewport bottom so the board never
  // pushes the page into a vertical scroll — regardless of the chrome above it.
  // Columns scroll internally (per-column), the page itself does not. On a phone
  // the bottom bar covers the lower edge of the screen, so its height (published
  // as --phone-bar-height, 0 elsewhere) is left free too.
  const boardRef = useRef<HTMLDivElement>(null);
  const [boardHeight, setBoardHeight] = useState(
    `calc(100dvh - 56px - ${PHONE_BAR_HEIGHT_CSS})`
  );
  useLayoutEffect(() => {
    const update = () => {
      if (boardRef.current) {
        const top = Math.round(boardRef.current.getBoundingClientRect().top);
        setBoardHeight(
          `max(240px, calc(100dvh - ${top}px - ${PHONE_BAR_HEIGHT_CSS}))`
        );
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const onOpenCard = useCallback(
    (cardId: string) => {
      navigate(
        `/app/${companyId}/project-boards/${projectBoardId}/project/${cardId}`
      );
    },
    [navigate, companyId, projectBoardId]
  );

  const handleCreateProject = () => {
    dispatch(setModalxComponent("ProjectForm"));
    dispatch(setModalxComponentProps({ projectBoardId }));
  };

  const handleShareBoard = () => {
    dispatch(setModalxComponent("ShareBoardForm"));
    dispatch(setModalxComponentProps({ projectBoardId }));
  };

  const openMoveToBoard = (cardId: string) => {
    dispatch(setModalxStyle("small"));
    dispatch(
      setModalxOptions({
        title: t("project_move_select_board", {
          defaultValue: "Move to board",
        }),
        size: "small",
      })
    );
    dispatch(setModalxComponent("MoveProjectToBoardForm"));
    dispatch(
      setModalxComponentProps({ cardId, fromBoardId: projectBoardId })
    );
  };

  const onDragStart = (start: DragStart) => {
    setIsDraggingCard(start.type === "CARD");
  };

  const onDragEnd = (result: DropResult) => {
    setIsDraggingCard(false);
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    // An optimistic (not-yet-created) card/list has a temp id; moving it — or
    // dropping a card into a not-yet-created column — would PATCH the server
    // with an id it doesn't know. Skip until the create resolves.
    if (draggableId.startsWith("temp")) return;

    // Pipedrive-style bottom drop zones (outcome / move to).
    if (type === "CARD") {
      const outcome = parseOutcomeDropId(destination.droppableId);
      if (outcome) {
        dispatch(
          setCardOutcomeThunk({
            companyId,
            cardId: draggableId,
            outcome,
          })
        );
        return;
      }
      if (destination.droppableId === ACTION_MOVE_TO) {
        openMoveToBoard(draggableId);
        return;
      }
    }

    if (type !== "COLUMN" && destination.droppableId.startsWith("temp")) return;
    if (type === "COLUMN") {
      dispatch(
        moveColumnThunk({
          companyId,
          columnId: draggableId,
          fromIndex: source.index,
          toIndex: destination.index,
        })
      );
    } else {
      setFlashColumnId(destination.droppableId);
      window.setTimeout(() => setFlashColumnId(null), 300);
      dispatch(
        moveProjectThunk({
          companyId,
          cardId: draggableId,
          toColumn: destination.droppableId,
          toIndex: destination.index,
        })
      );
    }
  };

  // Show the skeleton on first load / board switch (loading with no columns
  // yet). A same-board refetch (filter change) keeps the board visible instead.
  const showSkeleton = isLoading && columnOrder.length === 0;
  // A failed load with nothing to show gets an error + retry, not a blank board.
  const showError = !isLoading && isError && columnOrder.length === 0;

  return (
    <div
      ref={boardRef}
      className="flex flex-col overflow-hidden bg-[var(--p-color-bg)]"
      style={{ height: boardHeight }}
    >
      <ProjectBoardHeader
        companyId={companyId}
        boardId={projectBoardId}
        boardName={boardName}
        dealCount={dealCount}
        selectedUser={filtersValue.user}
        onUserFilterChange={handleFilterChange}
        onOpenAgenda={() => setAgendaOpen(true)}
        onShare={handleShareBoard}
        onCreateCard={handleCreateProject}
      />

      {/* Board */}
      {showSkeleton && <BoardSkeleton />}
      {showError && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
          <p className="text-sm text-[var(--p-color-text-secondary)]">
            {t("board_load_failed", {
              defaultValue: "Couldn't load this board.",
            })}
          </p>
          <button
            type="button"
            onClick={() =>
              dispatch(fetchProjectBoard(companyId, projectBoardId, filtersValue))
            }
            className="rounded-md bg-[var(--p-color-bg-fill-brand)] px-3 py-2 text-sm font-semibold text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)]"
          >
            {t("retry", { defaultValue: "Retry" })}
          </button>
        </div>
      )}
      {!showSkeleton && !showError && (
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="relative flex min-h-0 flex-1 flex-col">
            <Droppable droppableId="board" type="COLUMN" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`flex min-h-0 flex-1 items-start gap-3 px-4 pt-3 ${
                    isDraggingCard
                      ? "project-board-hide-scrollbar overflow-x-hidden pb-28 max-md:pb-40"
                      : "overflow-x-auto pb-4"
                  }`}
                >
                  {columnOrder.map((colId, index) => (
                    <BoardColumn
                      key={colId}
                      columnId={colId}
                      index={index}
                      companyId={companyId}
                      boardId={projectBoardId}
                      onOpenCard={onOpenCard}
                      flash={flashColumnId === colId}
                    />
                  ))}
                  {provided.placeholder}
                  <AddListComposer
                    onAdd={(name) =>
                      dispatch(
                        addColumnThunk({
                          companyId,
                          boardId: projectBoardId,
                          name,
                        })
                      )
                    }
                  />
                </div>
              )}
            </Droppable>
            {/* Always mounted (hit-testable). Opacity toggles with drag. */}
            <OutcomeDropBar visible={isDraggingCard} />
            <BoardAgendaPanel
              open={agendaOpen}
              onClose={() => setAgendaOpen(false)}
              onOpenCard={onOpenCard}
              companyId={companyId}
              boardId={projectBoardId}
            />
          </div>
        </DragDropContext>
      )}
    </div>
  );
}

export default ProjectBoardContainer;
